import { useState } from "react";
import { formatCad, DOSING_BANDS } from "@/data/catalog";
import { SAFETY_NOTE } from "@/data/policies";
import { measureSentence } from "@/components/MeasureCalc";
import { BuyButton } from "@/components/BuyButton";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n/LanguageContext";
import { money } from "@/components/CartTotals";

const Calc = ({ dram }) => {
  const { weightLb, setWeightLb } = useCart();
  const { t } = useLang();
  const [unit, setUnit] = useState("lb");
  const [raw, setRaw] = useState(weightLb ? String(weightLb) : "");
  const set = (v, u = unit) => { setRaw(v); const n = parseFloat(v); setWeightLb(n > 0 ? (u === "kg" ? n * 2.20462 : n) : 0); };
  return (
    <div className="calc" data-testid="measure-calc" data-no-translate>
      <div className="in">
        <input aria-label={t("Your dog's weight", "Le poids de votre chien")} data-testid="measure-calc-input" inputMode="decimal" placeholder="45" value={raw}
          onChange={(e) => set(e.target.value.replace(/[^0-9.]/g, ""))}
          style={{ width: 56, background: "transparent", border: 0, padding: "0 12px", color: "var(--cream)", fontSize: 16, fontWeight: 500, outline: "none" }} />
        <button type="button" onClick={() => { const u = unit === "lb" ? "kg" : "lb"; setUnit(u); set(raw, u); }} data-testid="measure-calc-unit" style={{ height: "100%" }}>
          <em>{unit.toUpperCase()}</em>
        </button>
      </div>
      <div className="out" data-testid="measure-calc-result">
        {measureSentence(weightLb > 0 ? weightLb : 45, t, dram ? null : 60)}
      </div>
    </div>
  );
};

const Opt = ({ id, on, dim, label, tag, right, onClick }) => (
  <button type="button" role="radio" aria-checked={on} aria-disabled={dim} disabled={dim} onClick={onClick} data-testid={`buy-option-${id}`}
    className={`opt${on ? " on" : ""}${dim ? " dim" : ""}`} style={{ textAlign: "left", width: "100%" }}>
    <span className="dot" />{label}{tag && <span className="tag">{tag}</span>}<span className="r">{right}</span>
  </button>
);

const Acc = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div role="button" tabIndex={0} aria-expanded={open} onClick={() => setOpen(!open)} onKeyDown={(e) => e.key === "Enter" && setOpen(!open)} data-testid={testid} style={{ cursor: "pointer" }}>
        {title} <span>{open ? "−" : "+"}</span>
      </div>
      {open && <p style={{ padding: "12px 2px 16px", fontSize: 14, color: "var(--text)", borderBottom: "1px solid var(--line)" }}>{children}</p>}
    </>
  );
};

const Icon = ({ d }) => <svg viewBox="0 0 24 24" aria-hidden="true">{d}</svg>;

export const BuyPanel = ({ p }) => {
  const [qty, setQty] = useState(1);
  const { t } = useLang();
  const dram = p.id === "discovery_dram";
  const two = Math.round(p.priceCents * 2 * 0.9);
  const openChat = () => document.querySelector('[data-testid="chat-open"]')?.click();
  const inside = dram ? "Three 10 g drams: Trailhead Vigor (cordyceps), Riverbend Calm (reishi) and The Forester's Guard (turkey tail)." : `${p.sourcing.mushroom}. ${p.sourcing.origin}. ${p.vessel}.`;
  return (
    <div className="info" data-testid="buy-panel">
      <span className="best" data-testid="product-pill">{p.pagePill}</span>
      <h1 data-testid="product-name">{p.name}</h1>
      <div className="latin" data-testid="product-subtitle">{p.subtitle.split(" · ").map((s, i, all) => <span key={s}>{i ? " · " : ""}{all[2] === "mushroom powder" && i === 1 ? <i>{s}</i> : s}</span>)}</div>
      <p className="lede"><b>{p.lead}</b> {p.leadRest}</p>
      <div className="pricebar"><span className="p" data-testid="product-price">{formatCad(p.priceCents)}</span><span className="s">{dram ? "Three drams · 3 × 10 g" : "60 g · 60 daily measures"}</span></div>
      <Calc dram={dram} />
      {!dram && (
        <div className="opts" role="radiogroup" aria-label="Purchase options" data-testid="buy-options">
          <Opt id="one" on={qty === 1} label="One jar" right={money(p.priceCents)} onClick={() => setQty(1)} />
          <Opt id="two" on={qty >= 2} label="Two jars" tag="Save 10%" right={money(two)} onClick={() => setQty(2)} />
          <Opt id="standing" dim label="Standing Order" tag="Coming soon · save 10%" right={money(Math.round(p.priceCents * 0.9))} />
        </div>
      )}
      <div className="buy">
        <div className="qty" data-testid="qty-stepper">
          <button type="button" aria-label="Decrease quantity" data-testid="qty-dec" onClick={() => setQty(Math.max(1, qty - 1))}><span>−</span></button>
          <span data-testid="qty-value" aria-live="polite">{qty}</span>
          <button type="button" aria-label="Increase quantity" data-testid="qty-inc" onClick={() => setQty(Math.min(12, qty + 1))}><span>+</span></button>
        </div>
        <BuyButton productIds={p.id} qty={qty} className="btn btn-primary" testid="product-buy"
          label={t(`Add to cart · ${formatCad(p.priceCents * qty).replace(" CAD", "")}`, `Ajouter au panier · ${formatCad(p.priceCents * qty).replace(" CAD", "")}`)} />
      </div>
      <div className="assure" data-testid="reassurance-row">
        <div><Icon d={<><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" /><circle cx="7" cy="17" r="1.6" /><circle cx="17" cy="17" r="1.6" /></>} />Packed in 2–4 days</div>
        <div><Icon d={<path d="M4 12l5 5L20 6" />} />Free shipping over $125</div>
        <div><Icon d={<><path d="M4 9h12a4 4 0 0 1 0 8H9" /><path d="M8 5L4 9l4 4" /></>} />30-day returns, unopened</div>
      </div>
      <div className="acc" data-testid="product-accordions">
        <Acc title="What's in the jar" testid="acc-inside">{inside}</Acc>
        <Acc title="How to use" testid="acc-use">
          {`One measure a day, by weight. Sprinkle it on their food or mix it in, whatever your dog prefers. ${DOSING_BANDS.map((b, i) => `${i === 3 ? "Over 90 lb" : `Up to ${b.maxLb} lb`}: ${b.label}`).join(" · ")}.`}
        </Acc>
        <Acc title="Safety" testid="acc-safety">{SAFETY_NOTE}</Acc>
      </div>
      <div className="ask">Questions? <button type="button" onClick={openChat} data-testid="product-ask" style={{ color: "var(--brass)" }}>Ask the Apothecary</button></div>
    </div>
  );
};
